import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AppComponentName, ExportData, ExportDataCriteria, ExportSaveAs, InterpolationInterval, InterpolationMethod, Message, MessageActionType, MessageType, Sensor, SensorReadingType, TimeZoneDisplay } from '@models';
import { ChartInfo } from '@models/Dashboard/chartData.model';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { UserService } from '@services/user.service';
import { Charting } from 'app/shared/classes/charting';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Chart, ChartConfiguration, ChartOptions } from 'chart.js';
import 'chartjs-adapter-moment';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

@Component({
  standalone: false,
  selector: 'mini-chart',
  templateUrl: './mini-chart.component.html',
  styleUrls: ['./mini-chart.component.scss']
})

export class MiniChartComponent implements OnInit {

  @Input() sensors: Sensor[];
  @Input() sensorReadings: SensorReadingInfo[];
  @Input() sensorReadingTypes: SensorReadingType[];
  @Input() currentEventName: string;

  charting = new Charting();
  
  modalDragOn: boolean = false;
  exportOptionsModalRef: BsModalRef;
  username = 'unknown';

  bigChartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: true,
    showLegend: true,
    showXAxisLabel: true,
    xAxisLabel: 'Date-Time',
    showYAxisLabel: true,
    yAxisLabel: 'Value',
    autoScale: true,
    scaleYMin: null,
    scaleYMax: null,
    axisFormat: null,
    legendTitle: 'Sensors & Reading Types',
    timeline: true,
    colorScheme: {
      domain: [ '#42f55a', '#046dd6', '#800080', '#ffc107', '#fa0f0f' ]
    }
  };

  ngxChartData: any[] = [];
  ngxChartView: any[] = [1200, 420];
  //chartInfos: ChartInfo[] = [];
  modalRef: BsModalRef;
  
  //---------

  chartType: ChartConfiguration['type'] = 'line';
  chartjsData: any[] = [ ];
  chartjsLabels: ChartConfiguration['data']['labels'] = ['January', 'February', 'March', 'April', 'May', 'June', 'July'];

  chartjsOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    elements: {
      line: {
        borderWidth: 1,
        tension: 0.4
      },
      point: {
        radius: 0,
        hitRadius: 0,
        hoverRadius: 0
      }
    },
    scales: {
      x: {
        type: 'time',
        display: false,
        grid: {
          display: false
        },
        time: {
          unit: 'day'
        }
      },
      y: {
        beginAtZero: true,
        display: false,
        grid: {
          display: false
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

      
  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.initChartInfo();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  ngOnChanges() {
    this.initChartInfo();
  }


  initChartInfo() {
    var sensorUUIDs = this.sensorReadings.map(x => x.uuid).filter((value, index, self) => {
      return self.indexOf(value) === index;
    });
    
    this.sensors = [];
    sensorUUIDs.forEach(uuid => {
      var sensor: Sensor = {
        id: 0,
        uuid: uuid,
        name: '',
        sensorReadingTypes: [],
        sensorReadingTypeNames: '',
        checked: false,
        modifiedDate: undefined,
        userGroupId: 0,
        userName: '',
        virtualSensor: undefined,
        sensorCalibrationHistory: null,
      };
      this.sensors.push(sensor);
    });
    
    var sensorReadingTypes = this.sensorReadings.map(x => x.type).filter((value, index, self) => {
      return self.indexOf(value) === index;
    });

    this.sensorReadingTypes = [];
    sensorReadingTypes.forEach(type => {
      var sensorReadingType: SensorReadingType = {
        id: 0,
        type: type
      };
      this.sensorReadingTypes.push(sensorReadingType);
    });

    this.updateChartData();
  }


  updateChartData(): void {
    if (this.sensors != undefined && this.sensorReadingTypes != undefined) {

      this.chartjsData = [];
      this.chartjsLabels = [];
      this.ngxChartData = [];

      this.sensorReadingTypes.forEach(readingType => {
        this.sensors.forEach(sensor => {

          const legend = (sensor.name != undefined && sensor.name != '') ? sensor.name : sensor.uuid ;

          var sensorReadings = this.sensorReadings.filter(s => s.uuid == sensor.uuid && s.type == readingType.type && s.value != "nan" && s.value != "NaN")
                                                  .map(a => ({ name: new Date(a.createdDate), value: Number(a.value).toString() }));
                                                  //.map(a => ({ name: new Date(a.createdDate), value: Math.round(Number(a.value)).toString() }));

          // Normalize data to start from zero
          var minValue = Math.min(...sensorReadings.map(o => Number(o.value)));
          sensorReadings.forEach(x => x.value = (Number(x.value) - minValue).toString() );

          const newData = {
            name: readingType.type + ', ' + legend,
            series: sensorReadings,
            uuid: sensor.uuid
          };

          if (newData.series.length > 0) {
            var newJsDataSet = newData.series.map(s => {
              return {
                x: s.name,  // Use Date object directly
                y: Number(s.value)
              };
            });

            // Add point at beginning of dataset for better line rendering
            const firstPoint = newJsDataSet[0];
            const newFirstPoint = {
              x: new Date(moment().subtract(1441, 'minute').toDate()), // 24 hours and 1 minute before time period
              y: firstPoint.y
            };

            newJsDataSet.unshift(newFirstPoint);
            newData.series.unshift({ name: newFirstPoint.x, value: firstPoint.y.toString() });

            // Add point at end of dataset for better line rendering
            const lastPoint = newJsDataSet[newJsDataSet.length - 1];
            const newLastPoint = {
              x: new Date(moment().add(1, 'minute').toDate()), // 1 minute after time period
              y: lastPoint.y
            };

            newJsDataSet.push(newLastPoint);
            newData.series.push({ name: newLastPoint.x, value: lastPoint.y.toString() });

            // Populate ngx-charts ngxChartData
            this.ngxChartData.push(newData);

            // Populate Chart.js chartData and chartLabels with overall readings but not with the details
            if (!readingType.type.toLowerCase().includes('details')) {
              const chartJsDataset = {
                data: newJsDataSet,
                label: newData.name,
                borderColor: this.getColorForReadingType(readingType.type),
                backgroundColor: 'transparent',
                fill: false,
                tension: 0
              };
              this.chartjsData.push(chartJsDataset);
            }

            // Set labels from the first dataset (all should have same timestamps)
            if (this.chartjsLabels.length === 0) {
              this.chartjsLabels = newData.series.map(s => s.name);
            }
          }
        });
      });

    }
  }


  private getColorForReadingType(readingType: string): string {
    const colors = [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ];

    if (readingType.toLowerCase().startsWith('irrigat')) {
      return '#046dd6';
    }

    return '#42f55a';
  }


  showDataPointsOnChart(chart) {
    this.charting.showDataPointsOnChart(chart);
  }

  
  openBigChart(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { backdrop: false, ignoreBackdropClick: false } );
  }



}
